import {
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  useMutation,
} from "@tanstack/react-query";
import {
  fetchSipPlans,
  fetchSwpPlans,
  getSTPPlans,
} from "../../api/clients/plan";
import { fetchSipDataById } from "../../api/transaction/sip";
import {
  fetchStpDataById,
  deleteStpTransactionApi,
} from "../../api/transaction/stp";
import {
  fetchSwpDataById,
  deleteSwpTransactionApi,
} from "../../api/transaction/swp";
import type { PlanType } from "../../types";
import { deleteSipTransactionApi } from "../../api/transaction";

/**
 * Centralised query-key factory for plan queries (SIP/XSIP, STP, SWP).
 *
 * Hierarchy:
 *   plan → planName → investmentAccountId → (list | infinite-list | details) → (page+pageSize | id)
 *
 * The nested structure enables partial invalidation at any level, e.g.
 *   - planKeys.all                                → every plan query
 *   - planKeys.plan("XSIP")                       → every XSIP query
 *   - planKeys.account("XSIP", accountId)         → every XSIP query for an account
 *   - planKeys.lists("XSIP", accountId)           → all paginated lists for that account
 *   - planKeys.list("XSIP", accountId, 1, 10)     → a single page
 *   - planKeys.detail("XSIP", accountId, id)      → a single plan's details
 */
export const planKeys = {
  all: ["plan"] as const,

  plan: (planName: PlanType) => [...planKeys.all, planName] as const,

  account: (planName: PlanType, investmentAccountId: string) =>
    [...planKeys.plan(planName), investmentAccountId] as const,

  lists: (planName: PlanType, investmentAccountId: string) =>
    [...planKeys.account(planName, investmentAccountId), "list"] as const,

  list: (
    planName: PlanType,
    investmentAccountId: string,
    page: number,
    pageSize: number,
    search?: string,
    sort?: Array<{ id: string; desc: boolean }>
  ) =>
    [
      ...planKeys.lists(planName, investmentAccountId),
      { page, pageSize, search, sort },
    ] as const,

  infiniteLists: (planName: PlanType, investmentAccountId: string) =>
    [
      ...planKeys.account(planName, investmentAccountId),
      "infinite-list",
    ] as const,

  infiniteList: (
    planName: PlanType,
    investmentAccountId: string,
    pageSize: number,
    search?: string,
    sort?: Array<{ id: string; desc: boolean }>
  ) =>
    [
      ...planKeys.infiniteLists(planName, investmentAccountId),
      { pageSize, search, sort },
    ] as const,

  details: (planName: PlanType, investmentAccountId: string) =>
    [...planKeys.account(planName, investmentAccountId), "details"] as const,

  detail: (planName: PlanType, investmentAccountId: string, id: string) =>
    [...planKeys.details(planName, investmentAccountId), { id }] as const,
};

// ---------------------------------------------------------------------------
// SIP (XSIP)
// ---------------------------------------------------------------------------

export const sipDataQueryOptions = (
  investmentAccountGuid: string,
  page?: number,
  pageSize?: number,
  search?: string,
  sort?: Array<{ id: string; desc: boolean }>
) =>
  queryOptions({
    queryKey: planKeys.list(
      "XSIP",
      investmentAccountGuid,
      page ?? 1,
      pageSize ?? 10,
      search,
      sort
    ),
    queryFn: () =>
      fetchSipPlans({
        investmentAccountGuid,
        page: page ?? 1,
        pageSize: pageSize ?? 10,
        search,
        sort,
      }),
    enabled: !!investmentAccountGuid,
    placeholderData: (data) => data,
  });

export function useSipData(
  investmentAccountGuid: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string,
  sort?: Array<{ id: string; desc: boolean }>
) {
  return useQuery(
    sipDataQueryOptions(investmentAccountGuid, page, pageSize, search, sort)
  );
}

export function useInfiniteSipData(variables: {
  investment_account_guid: string;
  pageSize: number;
  search?: string;
  sort?: Array<{ id: string; desc: boolean }>;
}) {
  const { investment_account_guid, pageSize = 10, search, sort } = variables;
  return useInfiniteQuery({
    queryKey: planKeys.infiniteList(
      "XSIP",
      investment_account_guid,
      pageSize,
      search,
      sort
    ),
    queryFn: ({ pageParam = 1 }) =>
      fetchSipPlans({
        investmentAccountGuid: investment_account_guid,
        page: pageParam,
        pageSize,
        search,
        sort,
      }),
    placeholderData: (data) => data,
    retry: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.results?.length === pageSize
        ? (pages.length ?? 0) + 1
        : undefined;
    },
    enabled: !!investment_account_guid,
  });
}

export function useSIP(variables: {
  investmentAccountId: string;
  id: string;
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: planKeys.detail(
      "XSIP",
      variables.investmentAccountId,
      variables.id
    ),
    queryFn: () =>
      fetchSipDataById(variables.investmentAccountId, variables.id),
    enabled:
      variables.enabled !== undefined
        ? variables.enabled
        : !!variables.investmentAccountId && !!variables.id,
  });
}

// ---------------------------------------------------------------------------
// SWP
// ---------------------------------------------------------------------------

export const swpPlansQueryOptions = (
  investmentAccountGuid: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string
) =>
  queryOptions({
    queryKey: planKeys.list(
      "SWP",
      investmentAccountGuid,
      page,
      pageSize,
      search
    ),
    queryFn: () => fetchSwpPlans(page, pageSize, investmentAccountGuid, search),
    placeholderData: (data) => data,
    enabled: !!investmentAccountGuid,
  });

export function useSwpData(
  investmentAccountGuid: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string
) {
  return useQuery(
    swpPlansQueryOptions(investmentAccountGuid, page, pageSize, search)
  );
}

export const useInfiniteSwpPlans = (
  variables: {
    investment_account_guid: string;
    pageSize: number;
    search?: string;
  },
  options?: { enabled?: boolean }
) => {
  const { enabled = true } = options || {};
  const { investment_account_guid, pageSize, search } = variables;
  return useInfiniteQuery({
    queryKey: planKeys.infiniteList(
      "SWP",
      investment_account_guid,
      pageSize,
      search
    ),
    queryFn: ({ pageParam = 1 }) =>
      fetchSwpPlans(pageParam, pageSize, investment_account_guid, search),
    placeholderData: (data) => data,
    retry: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.results?.length === pageSize
        ? (pages.length ?? 0) + 1
        : undefined;
    },
    enabled,
  });
};

export function useSWP(variables: { investmentAccountId: string; id: string }) {
  return useQuery({
    queryKey: planKeys.detail(
      "SWP",
      variables.investmentAccountId,
      variables.id
    ),
    queryFn: () =>
      fetchSwpDataById(variables.investmentAccountId, variables.id),
    enabled: !!variables.investmentAccountId && !!variables.id,
  });
}

// ---------------------------------------------------------------------------
// STP
// ---------------------------------------------------------------------------

export const stpPlansQueryOptions = (
  investment_account_guid: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string
) =>
  queryOptions({
    queryKey: planKeys.list(
      "STP",
      investment_account_guid,
      page,
      pageSize,
      search
    ),
    queryFn: () => getSTPPlans(page, pageSize, investment_account_guid, search),
    placeholderData: (data) => data,
    enabled: !!investment_account_guid,
  });

export function useSTPPlans(
  investmentAccountGuid: string,
  page: number = 1,
  pageSize: number = 10,
  search?: string
) {
  return useQuery(
    stpPlansQueryOptions(investmentAccountGuid, page, pageSize, search)
  );
}

export function useInfiniteSTPPlans(
  variables: {
    investment_account_guid: string;
    pageSize: number;
    search?: string;
  },
  options?: { enabled?: boolean }
) {
  const { enabled = true } = options || {};
  const { investment_account_guid, pageSize, search } = variables;
  return useInfiniteQuery({
    queryKey: planKeys.infiniteList(
      "STP",
      investment_account_guid,
      pageSize,
      search
    ),
    queryFn: ({ pageParam = 1 }) =>
      getSTPPlans(pageParam, pageSize, investment_account_guid, search),
    placeholderData: (data) => data,
    retry: false,
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.results?.length === pageSize
        ? (pages.length ?? 0) + 1
        : undefined;
    },
    enabled,
  });
}

export function useSTP(variables: { investmentAccountId: string; id: string }) {
  return useQuery({
    queryKey: planKeys.detail(
      "STP",
      variables.investmentAccountId,
      variables.id
    ),
    queryFn: () =>
      fetchStpDataById(variables.investmentAccountId, variables.id),
    enabled: !!variables.investmentAccountId && !!variables.id,
  });
}

export const useInvalidateSip = (investmentAccountId: string) => {
  const queryClient = useQueryClient();

  return {
    invalidate: () => {
      // Invalidate paginated SIP data (all pages)
      queryClient.invalidateQueries({
        queryKey: planKeys.lists("XSIP", investmentAccountId),
      });

      // Invalidate infinite scroll SIP data
      queryClient.invalidateQueries({
        queryKey: planKeys.infiniteLists("XSIP", investmentAccountId),
      });
    },
  };
};

export const useInvalidateStp = (investmentAccountId: string) => {
  const queryClient = useQueryClient();

  return {
    invalidate: () => {
      // Invalidate paginated STP data (all pages)
      queryClient.invalidateQueries({
        queryKey: planKeys.lists("STP", investmentAccountId),
      });

      // Invalidate infinite scroll STP data
      queryClient.invalidateQueries({
        queryKey: planKeys.infiniteLists("STP", investmentAccountId),
      });
    },
  };
};

export const useInvalidateSwp = (investmentAccountId: string) => {
  const queryClient = useQueryClient();

  return {
    invalidate: () => {
      // Invalidate paginated SWP data (all pages)
      queryClient.invalidateQueries({
        queryKey: planKeys.lists("SWP", investmentAccountId),
      });

      // Invalidate infinite scroll SWP data
      queryClient.invalidateQueries({
        queryKey: planKeys.infiniteLists("SWP", investmentAccountId),
      });
    },
  };
};

export const useDeletePlan = (
  investmentAccountGuid: string,
  planType: "XSIP" | "SWP" | "STP"
) => {
  const { invalidate: invalidateSIP } = useInvalidateSip(investmentAccountGuid);
  const { invalidate: invalidateSTP } = useInvalidateStp(investmentAccountGuid);
  const { invalidate: invalidateSWP } = useInvalidateSwp(investmentAccountGuid);
  const deleteTransaction = (() => {
    switch (planType) {
      case "XSIP":
        return deleteSipTransactionApi;
      case "SWP":
        return deleteSwpTransactionApi;
      case "STP":
        return deleteStpTransactionApi;
      default:
        return deleteSipTransactionApi;
    }
  })();
  const mutationKey = (() => {
    switch (planType) {
      case "XSIP":
        return ["transaction/sip/delete"] as const;
      case "SWP":
        return ["transaction/swp/delete"] as const;
      case "STP":
        return ["transaction/stp/delete"] as const;
      default:
        return ["transaction/order/delete"] as const;
    }
  })();

  return useMutation({
    mutationKey,
    mutationFn: deleteTransaction,
    onSuccess: () => {
      if (planType === "XSIP") {
        invalidateSIP();
      }
      if (planType === "SWP") {
        invalidateSWP();
      }
      if (planType === "STP") {
        invalidateSTP();
      }
    },
  });
};
